import type { ObjectKind } from '../types';

/** 영상·이미지를 모두 걸 수 있는 디스플레이(스크린/디지털 사인) */
export const VIDEO_MEDIA_KINDS = [
  'tv',
  'standbyme',
  'beam-screen',
  'led-sign',
] as const satisfies readonly ObjectKind[];

/** 출력물·그래픽만 붙이는 구조(배너/보드/벽면/POP 등) */
export const IMAGE_MEDIA_KINDS = [
  'x-banner',
  'brochure',
  'brochure-trifold',
  'desktop-brochure',
  'a-board',
  'a-board-wide',
  't-board',
  'easel-wood',
  'easel-metal',
  'giant-banner',
  'water-banner',
  'hanging-banner',
  'giant-pole',
  'pop-stand-a3',
  'pop-stand-a4',
  'pop-stand-clamp',
  'round-sign',
  'whiteboard',
  'fabric-frame',
  'scroll-banner',
  'lighting-backwall',
  'led-wood-wall',
  'block-wall',
  'block-gate',
  'lighting-gate',
  'arch-sign',
  'name-stand',
  'pipe-drape',
  'system-booth',
  'corner-booth',
  'score-stand',
] as const satisfies readonly ObjectKind[];

/** kind 기준 허용 모드. video = 영상+이미지, image = 이미지만 */
export type MediaMode = 'image' | 'video';

export type MediaPlayback = 'image' | 'video';

const videoSet = new Set<string>(VIDEO_MEDIA_KINDS);
const imageSet = new Set<string>(IMAGE_MEDIA_KINDS);

const VIDEO_EXT = /\.(mp4|webm|mov|m4v)(?:\s|$|\?|#)/i;
const IMAGE_EXT = /\.(jpe?g|png|webp|gif|bmp|avif)(?:\s|$|\?|#)/i;

export function getMediaMode(kind: ObjectKind): MediaMode | null {
  if (videoSet.has(kind)) return 'video';
  if (imageSet.has(kind)) return 'image';
  return null;
}

export function supportsMedia(kind: ObjectKind): boolean {
  return getMediaMode(kind) !== null;
}

export function allowsVideo(kind: ObjectKind): boolean {
  return getMediaMode(kind) === 'video';
}

export function allowsImage(kind: ObjectKind): boolean {
  return getMediaMode(kind) !== null;
}

/** 파일명·URL로 실제 재생 형태 판별. 영상 kind에 이미지가 오면 image. */
export function isVideoMediaSource(mediaName?: string, mediaUrl?: string): boolean {
  const candidates = [mediaName, mediaUrl].filter(Boolean) as string[];
  for (const source of candidates) {
    if (VIDEO_EXT.test(source)) return true;
  }
  for (const source of candidates) {
    if (IMAGE_EXT.test(source)) return false;
  }
  for (const source of candidates) {
    if (/video\//i.test(source)) return true;
    if (/image\//i.test(source)) return false;
  }
  return false;
}

export function resolveMediaPlayback(
  kind: ObjectKind,
  mediaName?: string,
  mediaUrl?: string,
): MediaPlayback | null {
  if (!mediaUrl) return null;
  if (allowsVideo(kind) && isVideoMediaSource(mediaName, mediaUrl)) return 'video';
  if (allowsImage(kind)) return 'image';
  return null;
}

export function mediaAccept(kind: ObjectKind): string {
  if (getMediaMode(kind) === 'video') {
    return 'video/mp4,video/webm,image/jpeg,image/png,image/webp';
  }
  return 'image/jpeg,image/png,image/webp';
}

export function mediaLabel(kind: ObjectKind): string {
  if (kind === 'brochure' || kind === 'brochure-trifold' || kind === 'desktop-brochure') return '표지 이미지';
  return getMediaMode(kind) === 'video' ? '영상/이미지 연결' : '이미지 연결';
}

export function mediaHint(kind: ObjectKind): string {
  return getMediaMode(kind) === 'video'
    ? 'MP4, WebM, JPG, PNG, WebP · 로컬 전용'
    : 'JPG, PNG, WebP · 로컬 전용';
}
